import { useMemo } from "react";
import * as Yup from "yup";
import { REQUIRED_MESSAGE, stringSchema } from "@/utils/form";
import { CarouselSlide } from "@/components/Common/CarouselForm/CarouselForm";
import { Formik } from "formik";
import { WalletAddressStepSlideForm } from "../WalletAddressStepSlideForm/WalletAddressStepSlideForm";
import { WalletType } from "@/utils/wallet";
import { PublicKey } from "@solana/web3.js";
import { AccountAddress } from "@aptos-labs/ts-sdk";
import { AuthMachineContext } from "@/store/machine/authMachine/authMachine";
import { AuthEventType } from "@/types/auth";
import { waitForever } from "@/utils";
import { XstateForm } from "@/components/Common/XstateForm/XstateForm";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";

export const WalletAddressStepSlide: React.FC<CarouselSlide> = ({ onPrev }) => {
  const authActorRef = AuthMachineContext.useActorRef();
  const meta = AuthMachineContext.useSelector((state) => state.context.profile?.user_metadata);
  const initialValues = useMemo(() => {
    return {
      walletAddress: meta?.wallet_address || ""
    } as WalletAddressStepFormType;
  }, [meta?.wallet_address]);

  return (
    <Formik<WalletAddressStepFormType>
      initialValues={initialValues}
      validationSchema={
        meta?.wallet_type === "solana"
          ? SolanaWalletAddressStepFormSchema
          : AptosWalletAddressStepFormSchema
      }
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);

        authActorRef.send({
          type: AuthEventType.SET_WALLET_ADDRESS,
          value: values.walletAddress
        });

        trackEvent(AnalyticsEventType.WALLET_CONNECTED_SUCCESSFULLY, {
          type: meta?.wallet_type
        });

        return waitForever();
      }}
      validateOnMount={true}
    >
      {(props) => {
        return (
          <XstateForm machineContext={AuthMachineContext}>
            <WalletAddressStepSlideForm
              {...props}
              onPrev={onPrev}
              walletType={(meta?.wallet_type || "solana") as WalletType}
            />
          </XstateForm>
        );
      }}
    </Formik>
  );
};

export type WalletAddressStepFormType = {
  submit?: string;
  walletAddress: string;
};

const SolanaWalletAddressStepFormSchema = Yup.object().shape({
  walletAddress: stringSchema()
    .required(REQUIRED_MESSAGE)
    .test({
      test: (value, ctx) => {
        const error = ctx.createError({
          message: "wallet ID not recognized - please try again"
        });

        try {
          const publicKey = new PublicKey(value);

          if (PublicKey.isOnCurve(publicKey)) {
            return true;
          }

          return error;
        } catch (e) {
          return error;
        }
      }
    })
});

const AptosWalletAddressStepFormSchema = Yup.object().shape({
  walletAddress: stringSchema()
    .required(REQUIRED_MESSAGE)
    .test({
      test: (value, ctx) => {
        const error = ctx.createError({
          message: "wallet ID not recognized - please try again"
        });

        try {
          const result = AccountAddress.isValid({ input: value, strict: true });

          if (result.valid) {
            return true;
          }

          return error;
        } catch (e) {
          return error;
        }
      }
    })
});
